import { Permission, useAuth } from '@/contexts/AuthContext';
import type { UseWorkspaceIntegrationsReturn } from '@/contexts/AuthContext/useWorkspaceIntegrations';
import { useDialog } from '@/contexts/DialogContext';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useToast } from '@/contexts/ToastContext';
import Skeleton from '@common/components/Skeleton';
import { MagnifyingGlass, } from '@phosphor-icons/react';
import { useState } from 'react';
import IntegrationCard from './integrations/IntegrationCard';
import IntegrationUrlModal from './integrations/IntegrationUrlModal';

/**
 * IntegrationsSettings 组件
 *
 * 集成状态数据源变更：
 * - 旧版本：从 profile.shopInfos (Shopify) 和 nangoIntegrations (其他平台) 混合获取
 * - 新版本：统一从 workspace_integrations 表通过 Supabase 拉取
 *
 * workspaceIntegrations 提供的接口：
 * - workspaceIntegrations.shopify - Shopify 集成数据
 * - workspaceIntegrations.bigCommerce - BigCommerce 集成数据
 * - workspaceIntegrations.googleAds - Google Ads 集成数据
 * - workspaceIntegrations.slack - Slack 集成数据
 * - workspaceIntegrations.woocommerce - WooCommerce 集成数据
 * - workspaceIntegrations.magento - Magento 集成数据
 *
 * 每个平台的集成数据结构：
 * - status: 'ACTIVE' | 'INACTIVE' | 'ERROR' - 连接状态
 * - connected_at: string - 连接时间
 * - nango_connection_id: string - Nango 连接 ID
 * - platform_shop_name: string | null - 平台店铺名称
 */

// 真正支持连接的应用
const SUPPORTED_APPS = [
    'shopify',
    'bigcommerce',
    'google_ads',
    'slack',
    'woocommerce',
    'magento',
    'aliexpress'
];

// 工具名称到 API provider 的映射
const TOOL_TO_PROVIDER: Record<string, string> = {
    'shopify': 'shopify',
    'bigcommerce': 'bigcommerce',
    'google_ads': 'google-ads',
    'slack': 'slack',
    'woocommerce': 'woocommerce',
    'magento': 'magento',
    'aliexpress': 'aliexpress',
};


const ALL_INTEGRATIONS = [
    {
        category: "E-commerce Platforms",
        tools: ["Shopify", "WooCommerce", "BigCommerce", "Magento", "Wix", "Squarespace", "PrestaShop", "Salesforce Commerce"]
    },
    {
        category: "POS Systems",
        tools: ["Square", "Toast", "Clover", "Lightspeed", "Shopify POS", "Vend", "Revel", "TouchBistro"]
    },
    {
        category: "Marketing & Advertising",
        tools: ["Meta Ads", "Google Ads", "TikTok Ads", "Snapchat Ads", "Pinterest Ads", "LinkedIn Ads", "Twitter Ads", "YouTube Ads"]
    },
    {
        category: "Email & SMS Marketing",
        tools: ["Klaviyo", "Mailchimp", "Attentive", "Postscript", "Omnisend", "Sendlane", "ActiveCampaign", "Constant Contact"]
    },
    {
        category: "Analytics & Data",
        tools: ["Google Analytics", "Segment", "Mixpanel", "Amplitude", "Heap", "Hotjar", "Looker", "Tableau"]
    },
    {
        category: "Payments & Accounting",
        tools: ["Stripe", "PayPal", "QuickBooks", "Xero", "Square Payments", "Braintree", "FreshBooks", "Wave"]
    },
    {
        category: "Customer Support & CRM",
        tools: ["Zendesk", "Intercom", "HubSpot", "Salesforce", "Gorgias", "Freshdesk", "Help Scout", "Pipedrive"]
    },
    {
        category: "Shipping & Fulfillment",
        tools: ["ShipStation", "ShipBob", "Deliverr", "EasyShip", "UPS", "FedEx", "USPS", "DHL"]
    },
    {
        category: "Team Collaboration",
        tools: ["Slack"]
    }
];

interface IntegrationsSettingsProps {
    workspaceIntegrations: UseWorkspaceIntegrationsReturn;
    mode?: 'onboarding' | 'admin';
}

const IntegrationsSettings: React.FC<IntegrationsSettingsProps> = ({
    workspaceIntegrations: {
        integrations: workspaceIntegrations,
        isLoading,
        refresh: refreshWorkspaceIntegrations,
        connect: connectPlatform,
        disconnect: disconnectPlatform
    }, mode = 'admin'
}) => {
    const { permissions } = useAuth();
    const canConnect = mode == 'onboarding' || permissions.checkPermissions([Permission.INTEGRATION_CONNECT]);
    const canDisconnect = mode == 'onboarding' || permissions.checkPermissions([Permission.INTEGRATION_DISCONNECT]);

    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('popular');
    const { onboardingData, updateOnboardingData } = useOnboarding();
    const { showToast } = useToast();
    const { showDialog } = useDialog();

    // 获取各个应用的连接状态
    // 现在统一从 workspace_integrations 表查询
    const {
        shopify: shopifyIntegration,
        bigCommerce: bigCommerceIntegration,
        googleAds: googleAdsIntegration,
        slack: slackIntegration,
        woocommerce: woocommerceIntegration,
        magento: magentoIntegration,
        aliexpress: aliexpressIntegration
    } = workspaceIntegrations;

    const [syncingTools, setSyncingTools] = useState<Set<string>>(new Set());
    const [connectingTool, setConnectingTool] = useState<string | null>(null);
    const [storeUrl, setStoreUrl] = useState('');
    const [registrationPending, setRegistrationPending] = useState<string | null>(null);

    // Popular integrations - 只包含真正支持连接的应用
    const POPULAR_INTEGRATIONS = ['Google Ads', 'Shopify', 'Aliexpress'];

    // 平台注册地址
    const REGISTRATION_URLS: Record<string, string> = {
        Shopify: 'https://www.shopify.com/free-trial',
        Aliexpress: 'https://seller.aliexpress.com/',
        'Google Ads': 'https://ads.google.com/',
    };

    // 需要输入 URL 的应用配置
    const URL_INPUT_CONFIG: Record<string, { prefix: string, suffix: string, placeholder: string }> = {
        'Shopify': {
            prefix: 'https://', suffix: '.myshopify.com', placeholder: 'your-store'
        },
        'BigCommerce': {
            prefix: 'https://store-', suffix: '.mybigcommerce.com', placeholder: 'your-store'
        },
    };

    // 检查应用是否支持连接
    const isSupported = (name: string) => SUPPORTED_APPS.includes(name.toLowerCase().replace(/\s+/g, '_'));

    /**
     * 获取应用的连接状态
     * 统一处理所有应用的连接状态判断逻辑
     * 现在从 workspace_integrations 表查询，检查对象的 connected 字段
     */
    const getConnectionStatus = (tool: string): 'idle' | 'syncing' | 'connected' => {
        if (syncingTools.has(tool)) return 'syncing';

        const normalizedTool = tool.toLowerCase().replace(/\s+/g, '_');
        const provider = TOOL_TO_PROVIDER[normalizedTool];

        switch (provider) {
            case 'shopify':
                return shopifyIntegration?.connected ? 'connected' : 'idle';
            case 'bigcommerce':
                return bigCommerceIntegration?.connected ? 'connected' : 'idle';
            case 'google-ads':
                return googleAdsIntegration?.connected ? 'connected' : 'idle';
            case 'slack':
                return slackIntegration?.connected ? 'connected' : 'idle';
            case 'woocommerce':
                return woocommerceIntegration?.connected ? 'connected' : 'idle';
            case 'magento':
                return magentoIntegration?.connected ? 'connected' : 'idle';
            case 'aliexpress':
                return aliexpressIntegration?.connected ? 'connected' : 'idle';
            default:
                return 'idle';
        }
    };

    /**
     * 处理应用连接
     * 根据 app 类型选择不同的连接方式：
     * - 需要输入 URL 的应用（Shopify, BigCommerce）：打开 URL 输入弹窗
     * - OAuth 应用（Google Ads, Slack, WooCommerce）：直接调用 OAuth 连接
     */
    const handleConnect = async (tool: string) => {
        if (!canConnect) {
            showToast('Permission Denied: You do not have permission to connect integrations', 'error');
            return;
        }

        const normalizedTool = tool.toLowerCase().replace(/\s+/g, '_');
        if (!isSupported(normalizedTool)) return;
        if (getConnectionStatus(tool) === 'connected') return;

        // Check if tool requires URL input
        if (URL_INPUT_CONFIG[tool]) {
            setConnectingTool(tool);
            setStoreUrl('');
            return;
        }

        // Direct connection using new unified API
        try {
            setSyncingTools(prev => new Set(prev).add(tool));

            const result = await connectPlatform(tool, undefined, {
                onSuccess: () => {
                    showToast(`Successfully connected to ${tool}`, 'success');
                    // Update onboarding data
                    if (updateOnboardingData) {
                        updateOnboardingData({ connectedIntegrations: [...(onboardingData.connectedIntegrations || []), tool] });
                    }
                    // 刷新集成列表
                    refreshWorkspaceIntegrations();
                    // 移除 syncing 状态
                    setSyncingTools(prev => {
                        const next = new Set(prev);
                        next.delete(tool);
                        return next;
                    });
                },
                onError: (errorMsg: string) => {
                    // 关闭弹窗时不显示提示，只停止加载动画
                    if (errorMsg !== 'WooCommerce connection dialog was closed') {
                        showToast(`Connection Failed: ${errorMsg}`, 'error');
                    }
                    // 移除 syncing 状态
                    setSyncingTools(prev => {
                        const next = new Set(prev);
                        next.delete(tool);
                        return next;
                    });
                }
            });

            // 如果返回 uiOpened 标志，说明是通过 Connect UI 的异步连接
            // 不显示成功提示，连接成功/失败会在 onEvent 回调中处理
            if (!result?.uiOpened) {
                showToast(`Successfully connected to ${tool}`, 'success');

                // Update onboarding data
                if (updateOnboardingData) {
                    updateOnboardingData({ connectedIntegrations: [...(onboardingData.connectedIntegrations || []), tool] });
                }

                setSyncingTools(prev => {
                    const next = new Set(prev);
                    next.delete(tool);
                    return next;
                });
            }
            // uiOpened 的情况保持 syncing 状态，等待 onEvent 回调
        } catch (error: any) {
            showToast(`${error.message || `Failed to connect to ${tool}`}`, 'error');
            setSyncingTools(prev => {
                const next = new Set(prev);
                next.delete(tool);
                return next;
            });
        }
    };

    const handleRegister = (tool: string) => {
        const url = REGISTRATION_URLS[tool] || `https://www.google.com/search?q=${encodeURIComponent(tool)}+signup`;
        window.open(url, '_blank', 'noopener,noreferrer');
        setRegistrationPending(tool);
    };

    /**
     * 处理需要输入 URL 的应用连接
     * 包括 Shopify 和 BigCommerce
     */
    const handleUrlConnection = async () => {
        if (!connectingTool || !storeUrl.trim()) return;

        try {
            setSyncingTools(prev => new Set(prev).add(connectingTool));

            if (connectingTool === 'Shopify') {
                // Shopify connection logic
                const url = new URL(window.location.href);
                url.searchParams.set('connect_success', 'shopify');
                await connectPlatform('shopify', {
                    shop_domain: `${storeUrl}.myshopify.com`,
                    redirect_url: url.toString()
                });
                showToast(`Successfully connected to Shopify`, 'success');
            } else if (connectingTool === 'BigCommerce') {
                // BigCommerce connection logic
                await connectPlatform('bigcommerce', {store_domain: `https://store-${storeUrl}.mybigcommerce.com/`});
                showToast('Successfully connected to BigCommerce', 'success');
            }

            // Update onboarding data
            if (updateOnboardingData) {
                updateOnboardingData({ connectedIntegrations: [...(onboardingData.connectedIntegrations || []), connectingTool] });
            }

            setConnectingTool(null);
            setStoreUrl('');
        } catch (error: any) {
            showToast(`Connection Failed: ${error.message || `Failed to connect to ${connectingTool}`}`, 'error');
        } finally {
            setSyncingTools(prev => {
                const next = new Set(prev);
                next.delete(connectingTool!);
                return next;
            });
        }
    };

    /**
     * 处理断开集成连接 - 使用 showDialog 显示确认弹窗
     */
    const handleDisconnect = async (tool: string) => {
        if (!canDisconnect) {
            showToast('Permission Denied: You do not have permission to disconnect integrations', 'error');
            return;
        }

        const confirmed = await showDialog({
            title: `Disconnect ${tool}?`,
            subtitle: 'Disconnecting will stop data synchronization. You can reconnect at any time.',
            confirmText: 'Disconnect',
            cancelText: 'Cancel',
            destructive: true,
        });

        if (confirmed) {
            performDisconnect(tool);
        }
    };

    /**
     * 执行断开集成连接操作
     * 使用统一的 disconnect API
     */
    const performDisconnect = async (tool: string) => {
        try {
            const normalizedTool = tool.toLowerCase().replace(/\s+/g, '_');

            // 使用统一的 disconnect API
            await disconnectPlatform(normalizedTool);

            // Update onboarding data
            if (updateOnboardingData) {
                updateOnboardingData({connectedIntegrations: (onboardingData.connectedIntegrations || []).filter(i => i !== tool)});
            }

            showToast(`${tool} Disconnected: Successfully disconnected from ${tool}`, 'success');
        } catch (error: any) {
            showToast(`Disconnect Failed: ${error.message || `Failed to disconnect from ${tool}`}`, 'error');
        }
    };

    const filteredIntegrations = ALL_INTEGRATIONS.map(cat => ({
        ...cat,
        tools: cat.tools.filter(t => t.toLowerCase().includes(searchQuery.toLowerCase()))
    })).filter(cat => cat.tools.length > 0);

    // Helper function to render integration card
    const renderIntegrationCard = (tool: string) => {
        const normalizedTool = tool.toLowerCase().replace(/\s+/g, '_');
        const supported = isSupported(normalizedTool);
        const status = getConnectionStatus(tool);
        const isSyncing = status === 'syncing';
        const isConnected = status === 'connected';
        const isPending = registrationPending === tool && !isConnected;

        return (
            <IntegrationCard
                key={tool}
                tool={tool}
                supported={supported}
                isConnected={isConnected}
                isSyncing={isSyncing}
                onConnect={() => handleConnect(tool)}
                onDisconnect={() => {
                    // Fire and forget - async operation without awaiting
                    handleDisconnect(tool).catch(err => {
                        console.error('Disconnect dialog error:', err);
                    });
                }}
                onRegister={() => handleRegister(tool)}
                onCancelPending={() => {
                    if (registrationPending === tool) setRegistrationPending(null);
                }}
                isPending={isPending}
                canConnect={canConnect}
                canDisconnect={canDisconnect}
            />
        );
    };

    // Helper function to render category
    const renderCategory = (cat: { category: string; tools: string[] }, idx: number) => {
        return (
            <div key={idx}>
                <h3 className="text-base font-bold text-text-main text-gray-900 mb-5 px-1">{cat.category}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 items-start">
                    {cat.tools.map(tool => renderIntegrationCard(tool))}
                </div>
            </div>
        );
    };

    // 骨架屏组件
    const renderSkeleton = () => (
        <div className="space-y-8">
            {/* Header Skeleton */}
            {mode === 'admin' && (
                <div className="space-y-4">
                    <Skeleton width="240px" height="42px" />
                </div>
            )}

            {/* Content Skeleton */}
            <div>
                {/* Tabs Skeleton */}
                <div className="flex items-center gap-6 border-b border-gray-100 mb-6">
                    <Skeleton width="80px" height="40px" />
                    <Skeleton width="120px" height="40px" />
                </div>

                {/* Cards Skeleton */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 items-start">
                    {Array.from({ length: 4 }).map((_, idx) => (
                        <div key={idx} className="flex flex-col p-4 rounded-xl border border-gray-200 h-full">
                            <div className="flex items-center gap-3">
                                <Skeleton width="40px" height="40px" />
                                <div className="min-w-0 flex-1">
                                    <Skeleton width="100%" height="20px" />
                                </div>
                            </div>
                            <div className="mt-auto pt-4">
                                <Skeleton width="100%" height="36px" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-8">
            {/* Header */}
            {mode === 'admin' && (
                <div className="space-y-4">
                    <div className="relative max-w-xs">
                        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-text-sub text-gray-400" size={18} weight="bold" />
                        <input
                            type="text"
                            placeholder="Search integrations..."
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#1A162F]/20 focus:border-[#1A162F] outline-none transition-all text-text-main text-gray-900 placeholder:text-text-sub/50"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            )}

            {/* Loading State */}
            {isLoading ? (
                renderSkeleton()
            ) : (
                /* Content */
                <div>
                    {/* Tabs - Only show when not searching */}
                    {!searchQuery && (
                        <div className="flex items-center gap-6 border-b border-gray-100 mb-6">
                            <button
                                onClick={() => setActiveTab('popular')}
                                className={`pb-3 text-sm font-semibold transition-all relative ${
                                    activeTab === 'popular'
                                        ? "text-[#1A162F]"
                                        : "text-text-sub hover:text-[#1A162F]"
                                }`}
                            >
                                Popular
                                {activeTab === 'popular' && (
                                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#1A162F] rounded-t-full" />
                                )}
                            </button>
                            <button
                                onClick={() => setActiveTab('all')}
                                className={`pb-3 text-sm font-semibold transition-all relative ${
                                    activeTab === 'all'
                                        ? "text-[#1A162F]"
                                        : "text-text-sub hover:text-[#1A162F]"
                                }`}
                            >
                                All Integrations
                                {activeTab === 'all' && (
                                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#1A162F] rounded-t-full" />
                                )}
                            </button>
                        </div>
                    )}

                    {filteredIntegrations.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-text-sub text-gray-500">
                            <MagnifyingGlass size={48} weight="bold" className="mb-4 opacity-20" />
                            <p className="text-lg font-medium text-text-sub text-gray-500">No integrations found</p>
                            <p className="text-sm">Try searching for something else</p>
                        </div>
                    ) : (
                        <div className="pb-10">
                            {/* Search Results or Active Tab Content */}
                            {searchQuery ? (
                                <div className="space-y-10">
                                    {filteredIntegrations.map((cat, idx) => renderCategory(cat, idx))}
                                </div>
                            ) : (
                                <>
                                    {activeTab === 'popular' ? (
                                        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 items-start">
                                                {POPULAR_INTEGRATIONS.map(tool => renderIntegrationCard(tool))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-10 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                            {filteredIntegrations.map((cat, idx) => renderCategory(cat, idx))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}

            <IntegrationUrlModal
                isOpen={!!connectingTool && !!URL_INPUT_CONFIG[connectingTool]}
                onClose={() => {
                    setConnectingTool(null);
                    setStoreUrl('');
                }}
                toolName={connectingTool || ''}
                urlConfig={connectingTool ? URL_INPUT_CONFIG[connectingTool] : {
                    prefix: '', suffix: '', placeholder: ''
                }}
                storeUrl={storeUrl}
                onStoreUrlChange={setStoreUrl}
                onConfirm={handleUrlConnection}
                isLoading={connectingTool ? syncingTools.has(connectingTool) : false}
            />
        </div>
    );
};

export default IntegrationsSettings;
